/**
 * The one place the "a disagreeing row lists what it is made of" rule lives.
 *
 * Both kinds of disagreement the page can carry — an outcome metric spanning several optimisation
 * objectives, and a provider dimension spanning several values — pass through the same cap so a
 * reader learns one rule instead of four. Surfaces differ only in how they lay the result out.
 */
import { formatSpend } from '@/features/creative-intelligence/utils/creative-intelligence-format'
import { resultTypeSingularLabel } from '@/features/agents/agent-panel/agent-panel-employees/channel-insights-result-type-label'
import type { GroupAggregateRow } from '@/features/creative-performance/creative-performance-view-helpers'
import type { ICreativePerformanceRow } from '@/api/creative-intelligence/get-creative-performance-query'

/** One provider dimension as the row carries it — a single value, several, or unset. */
type IResolvedDimension = NonNullable<
    NonNullable<ICreativePerformanceRow['dimensions']>[keyof NonNullable<ICreativePerformanceRow['dimensions']>]
>

/** One objective's share of a row's cost per result, already labelled for display. */
export interface ICostPerResultContribution {
    key: string
    label: string
    costPerResultMicros: string | null
}

/**
 * The row's cost per result, one entry per objective its ads optimised for.
 *
 * Returns a SINGLE entry for a row that ran one objective, so a caller can branch on length rather
 * than on `mixedObjectives` — and empty for a row carrying no breakdown at all (a response cached
 * before the field existed), which degrades to the single blended figure.
 */
export function costPerResultContributions(row: GroupAggregateRow): ICostPerResultContribution[] {
    return (row.resultBreakdown ?? []).map((contribution) => ({
        key: contribution.resultActionType,
        label: resultTypeSingularLabel(contribution.resultActionType),
        costPerResultMicros: contribution.costPerResultMicros,
    }))
}

/**
 * An objective's figure, or an em dash where that objective has produced nothing to divide by.
 *
 * Cents are kept below the compact threshold, unlike the grid's whole-unit `formatSpend`. Rounding is
 * right for spend — nobody needs the cents on €1,203 — but wrong for a per-objective cost, where the
 * cheap objectives are exactly the ones a breakdown surfaces: a real €0.42 cost per link click
 * rendering as `€0` is a confidently wrong number.
 */
export function formatContribution(contribution: ICostPerResultContribution, currency: string) {
    if (contribution.costPerResultMicros === null) return '—'
    const major = Number(contribution.costPerResultMicros) / 1_000_000
    // At and above the compact threshold `formatSpend` already reads well (`€1.2K`) and the cents are
    // noise; below it every contribution shows them, so a stacked list has one precision throughout.
    if (Math.abs(major) >= 1_000) return formatSpend(Number(contribution.costPerResultMicros), currency)
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(major)
    } catch {
        return `${major.toFixed(2)} ${currency}`
    }
}

/**
 * How many contributing values a width-constrained surface shows inline.
 *
 * Three is the point where a card row still reads as a value rather than as a list. The detail
 * panel is not width-constrained and deliberately does not apply this.
 */
export const INLINE_CONTRIBUTION_LIMIT = 3

export interface IContributionOverflow {
    /** How many contributors are not shown inline. Always at least 1 when present. */
    count: number
    /** Every contributor's label, shown in the disclosure — including the ones already inline. */
    labels: string[]
    /**
     * True when the SERVER already truncated the list, so `labels` is itself partial. The disclosure
     * must say so: a truncated list presented as complete tells the reader a creative spans twelve
     * values when it spans forty.
     */
    serverTruncated: boolean
}

/**
 * Split a contributor list into what is shown inline and what is disclosed behind an affordance.
 *
 * `overflow` is `null` when everything fits, so a caller can render the affordance on presence
 * rather than on a count comparison it would have to repeat.
 */
export function capContributions<T>(
    contributions: readonly T[],
    label: (contribution: T) => string,
    options: { serverTruncated?: boolean; totalCount?: number } = {}
) {
    const shown = contributions.slice(0, INLINE_CONTRIBUTION_LIMIT)
    // The server's own count where it sent one, so a list it truncated still reports how many the
    // creative actually spans rather than how many survived transmission.
    const total = options.totalCount ?? contributions.length
    const hidden = Math.max(0, total - shown.length)
    const overflow: IContributionOverflow | null =
        hidden > 0 || options.serverTruncated
            ? { count: hidden, labels: contributions.map(label), serverTruncated: options.serverTruncated ?? false }
            : null
    return { shown, overflow }
}

/** The disclosure's text — the full list, and an admission when the server did not send all of it. */
export function contributionOverflowTitle(overflow: IContributionOverflow) {
    const list = overflow.labels.join(', ')
    return overflow.serverTruncated ? `${list} — and more not shown` : list
}

/**
 * The cost-per-result breakdown as card parts, or nothing when the row has one answer.
 *
 * Lives beside the cap rather than in the view so the card, the grouped header and any later surface
 * that wants stacked lines all get the same shape from one call.
 */
export function costPerResultCardParts(row: GroupAggregateRow, currency: string) {
    const contributions = costPerResultContributions(row)
    if (contributions.length <= 1) return undefined
    const { shown, overflow } = capContributions(contributions, (part) => part.label)
    return {
        parts: shown.map((part) => ({
            key: part.key,
            label: part.label,
            value: formatContribution(part, currency),
        })),
        ...(overflow ? { overflow } : {}),
    }
}

/**
 * A provider dimension's contributing values, capped, or `null` when the row has one value.
 *
 * Kept in ALPHABETICAL order — the order the server resolved them in. A dimension carries no
 * per-value spend, so ranking these the way the outcome contributions are ranked would invent a
 * precedence the data cannot support.
 */
export function dimensionContributions(dimension: IResolvedDimension | undefined) {
    if (!dimension || dimension.state !== 'mixed') return null
    const values = dimension.values ?? []
    if (values.length === 0) return null
    return capContributions(values, (value) => value.label, {
        serverTruncated: dimension.valuesTruncated ?? false,
        totalCount: dimension.valueCount,
    })
}

/** The cost-per-result contributions a width-constrained cell had to leave out, or `null`. */
export function costPerResultOverflow(row: GroupAggregateRow) {
    const contributions = costPerResultContributions(row)
    if (contributions.length <= 1) return null
    return capContributions(contributions, (part) => part.label).overflow
}

/**
 * The short "showing part of a list" marker, or `null` when the list is whole.
 *
 * Returned separately from the joined text so a cell can render it OUTSIDE its truncating span. A
 * table row is fixed-height with user-resizable columns, so anything inside that span is clipped
 * first — and clipping this particular token turns a partial list back into one that looks complete.
 */
export function contributionMarker(overflow: IContributionOverflow | null | undefined) {
    if (!overflow) return null
    return overflow.count > 0 ? `+${overflow.count}` : '+…'
}

/**
 * What a width-constrained dimension cell prints, and the marker that goes beside it.
 *
 * Joined rather than stacked: a table row is fixed-height and its columns are user-resizable, so
 * stacking would either clip or force variable row heights through a virtualised list.
 */
export function dimensionCell(dimension: IResolvedDimension | undefined) {
    const capped = dimensionContributions(dimension)
    if (!capped) return { text: dimension?.label ?? '—', marker: null, overflow: null }
    return {
        text: capped.shown.map((value) => value.label).join(', '),
        marker: contributionMarker(capped.overflow),
        overflow: capped.overflow,
    }
}

/** The cell's text alone — the column model's `format`, which must return a plain string. */
export function dimensionCellText(dimension: IResolvedDimension | undefined) {
    return dimensionCell(dimension).text
}
